/**
 * @file UserAccountView.js
 * @description Component for viewing and editing user account details, including personal information, security roles, and access permissions.
 */
import '../user-account/UserAccountView.scss';
import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import { Dropdown } from '../helpers/Dropdown';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { useAuthContext } from '../../store/Contexts';
import PasswordInput from '../ui/PasswordInput';
import {
    fetchAdminUserById,
    fetchAgencies,
    updateAdminUser,
    deleteAdminUserById,
    createAdminUser,
} from '../../services/systemService';
import {
    fetchTenantSecurityRoles,
    fetchClients,
} from '../../services/clientService';
import { isValidEmailAddress, isValidPhoneNumber } from '../../utils/validate';
import PhoneInput from 'react-phone-number-input/input';
import {
    initiateMultitenantUserOnboarding,
    resetUser,
} from '../../services/individualService';
import { sendForgotPasswordEmail } from '../../services/authService';
import AlertBox from '../ui/no_data_indicators/AlertBox';
import { hasSequentialChars } from '../../utils/utils';
import {
    TenantUserStatusEnum,
    SecurityAttributes,
    LogEventType,
} from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import { parseISO } from 'date-fns';
import { ROUTES } from '../../constants/routes';
import AgencyMultiSelect from '../shared/asyncSearchableMultiSelect/AgencyMultiSelect';

const ALL_ASSIGNED_AGENCIES_VALUE = 'all-assigned';
import Tooltip from '../ui/tooltips/Tooltip';
import { NOTIFICATION_METHOD } from '../../utils/data/enums';
import { getText } from '../../i18n';

const UserAccountView = () => {
    const { multitenantFlag, companyConfig, hasAttribute } = useAuthContext();
    const { userAccountId } = useParams();
    const location = useLocation();
    const isNewUserAccount = location.pathname.includes('add');
    const { showModal } = useContext(ModalContext);
    const navigate = useNavigate();
    const [userAccount, setUserAccount] = useState({});
    const topRef = useRef(null);
    const passwordRef = useRef(null);

    const [securityRolesOptions, setSecurityRolesOptions] = useState([]);
    const [allClientOptions, setAllClientOptions] = useState([]);
    const [clientOptions, setClientOptions] = useState([]);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [preferredName, setPreferredName] = useState('');
    const [pronouns, setPronouns] = useState('');
    const [language, setLanguage] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [securityRole, setSecurityRole] = useState({});
    const [selectedAgencies, setSelectedAgencies] = useState([]);
    const [agencyIdsWithAllClientAccess, setAgencyIdsWithAllClientAccess] =
        useState([]);
    const [clientAccessByAgency, setClientAccessByAgency] = useState({});
    const [selectedViewAgency, setSelectedViewAgency] = useState(
        ALL_ASSIGNED_AGENCIES_VALUE,
    );
    const [existingUserAgencies, setExistingUserAgencies] = useState([]);
    const [preToggleSnapshot, setPreToggleSnapshot] = useState(null);
    const [isSuperUser, setIsSuperUser] = useState(false);
    const [isAccountEnabled, setIsAccountEnabled] = useState(true);
    const [profilePicture, setProfilePicture] = useState('');
    const [password1, setPassword1] = useState('');
    const [password, setPassword] = useState('');
    const [passwordRequirements, setPasswordRequirements] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        repeats: false,
        sequential: false,
        patterns: false,
    });
    const [showPasswordRequirements, setShowPasswordRequirements] =
        useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isToggling, setIsToggling] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [isLocking, setIsLocking] = useState(false);

    const [registrationType, setRegistrationType] = useState('email');
    const [sendRegistrationOnCreate, setSendRegistrationOnCreate] =
        useState(true);

    const [passwordResetType, setPasswordResetType] = useState('email');

    const [isUnableToLoad, setIsUnableToLoad] = useState(false);
    const [isUnauthorized, setUnauthorized] = useState(false);
    const [accountAccessEndDate, setAccountAccessEndDate] = useState(null);
    const [initialAccountAccessEndDate, setInitialAccountAccessEndDate] =
        useState(null);

    const canEditForm = hasAttribute(SecurityAttributes.UserAccountFullAccess);
    const canResetPassword = hasAttribute(
        SecurityAttributes.UserAccountPasswordReset,
    );

    useEffect(() => {
        topRef.current.scrollIntoView({ behavior: 'auto' });

        if (!isNewUserAccount && userAccountId) {
            loadUserAccountDetails();
        } else {
            setFirstName('');
            setLastName('');
            setPhone('');
            setEmail('');
            setSecurityRole('');
            setSelectedAgencies([]);
            setAgencyIdsWithAllClientAccess([]);
            setClientAccessByAgency({});
            setSelectedViewAgency(ALL_ASSIGNED_AGENCIES_VALUE);

            loadDropdowns();
        }
    }, [userAccountId, isNewUserAccount]);

    const loadDropdowns = async () => {
        const secRoles = await fetchTenantSecurityRoles(0);

        const formattedSecRoles = secRoles.map(item => ({
            value: item.id,
            label: item.name,
        }));
        setSecurityRolesOptions(formattedSecRoles);

        const clients = await fetchClients([]);
        setAllClientOptions(clients);
        setClientOptions([]);
    };

    const loadUserAccountDetails = async () => {
        setIsLoading(true);
        try {
            const adminuser = await fetchAdminUserById(userAccountId);
            setFirstName(adminuser.firstName);
            setLastName(adminuser.lastName);
            setPreferredName(adminuser.preferredName || adminuser.firstName);
            setPronouns(adminuser.pronouns || '-');
            setLanguage(adminuser.locale || '-');
            setPhone(adminuser.phoneNumber);
            setEmail(adminuser.emailAddress);
            setIsAccountEnabled(
                adminuser.tenantUserStatusId == TenantUserStatusEnum.Active,
            );
            setProfilePicture(adminuser.avatarURL);
            setUserAccount(adminuser);
            setIsSuperUser(adminuser.isSuperUser || false);

            let accountAccessDateNoTime = null;

            if (adminuser.accountAccessEndDate)
                accountAccessDateNoTime =
                    adminuser.accountAccessEndDate.substring(0, 10);

            setAccountAccessEndDate(accountAccessDateNoTime);
            setInitialAccountAccessEndDate(accountAccessDateNoTime);

            const [secRoles, agencies, clients] = await Promise.all([
                fetchTenantSecurityRoles(0),
                fetchAgencies(),
                fetchClients([]),
            ]);

            setAllClientOptions(clients);

            const formattedSecRoles = secRoles.map(item => ({
                value: item.id,
                label: item.name,
            }));
            if (adminuser.securityRoleId != null) {
                const role = formattedSecRoles.find(
                    item => item.value === adminuser.securityRoleId,
                );
                setSecurityRole(role);
            }
            setSecurityRolesOptions(formattedSecRoles);

            const allAgencies = agencies.viewModels ?? [];
            const userAgencyIds = adminuser.agencies || [];
            const userAgencies = allAgencies.filter(a =>
                userAgencyIds.includes(a.id),
            );
            setSelectedAgencies(userAgencies);
            setExistingUserAgencies(adminuser.agencies || []);

            const allClientAccessIds =
                adminuser.agencyIdsWithAllClientAccess || [];
            setAgencyIdsWithAllClientAccess(allClientAccessIds);

            const clientsByAgency = {};
            userAgencyIds.forEach(agencyId => {
                const agencyClients = clients
                    .filter(c => c.agencyId === agencyId || agencyId === 0)
                    .map(c => ({ id: c.id, name: c.name, agency: c.agency }));

                if (allClientAccessIds.includes(agencyId)) {
                    clientsByAgency[agencyId] = agencyClients;
                } else {
                    clientsByAgency[agencyId] = agencyClients.filter(c =>
                        (adminuser.clients || []).includes(c.id),
                    );
                }
            });
            setClientAccessByAgency(clientsByAgency);

            const defaultViewAgency =
                userAgencies.length > 1
                    ? ALL_ASSIGNED_AGENCIES_VALUE
                    : userAgencies.length === 1
                      ? userAgencies[0].id
                      : ALL_ASSIGNED_AGENCIES_VALUE;
            setSelectedViewAgency(defaultViewAgency);

            updateVisibleClients(
                defaultViewAgency,
                clients,
                userAgencies,
                allClientAccessIds,
                clientsByAgency,
            );
        } catch (error) {
            console.error('Error loading user account details:', error);
            setIsUnableToLoad(true);
            if (error.status === 400 || error.status === 401) {
                setUnauthorized(true);
            }

            if (error?.response?.data?.detail) {
                Toast.error(error.response.data.detail);
            } else {
                Toast.error(getText('msg_aec6f51a0ff5'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSecurityRolesDropdown = value => {
        const role = securityRolesOptions.find(item => item.value === value);
        setSecurityRole(role);
    };

    const updateVisibleClients = (
        viewAgencyId,
        clients,
        agencies,
        allAccessIds,
        byAgency,
    ) => {
        const clientSource = clients || allClientOptions;
        if (viewAgencyId === ALL_ASSIGNED_AGENCIES_VALUE) {
            const agencyIds = agencies.map(a => a.id);
            const visible = clientSource
                .filter(
                    c =>
                        agencyIds.includes(c.agencyId) || agencyIds.includes(0),
                )
                .map(c => ({ id: c.id, name: c.name, agency: c.agency }));
            setClientOptions(visible);
        } else {
            const visible = clientSource
                .filter(c => c.agencyId === viewAgencyId || viewAgencyId === 0)
                .map(c => ({ id: c.id, name: c.name, agency: c.agency }));
            setClientOptions(visible);
        }
    };

    const handleAssignedAgenciesChange = newAgencies => {
        const validAgencies = newAgencies.filter(Boolean);

        if (validAgencies.length === 0 && newAgencies.length > 0) {
            return;
        }

        setSelectedAgencies(validAgencies);

        const newAgencyIds = validAgencies.map(a => a.id);
        const updatedByAgency = { ...clientAccessByAgency };
        Object.keys(updatedByAgency).forEach(key => {
            const numKey = Number(key);
            if (!newAgencyIds.includes(numKey)) {
                delete updatedByAgency[numKey];
            }
        });
        setClientAccessByAgency(updatedByAgency);

        const updatedAllAccess = agencyIdsWithAllClientAccess.filter(id =>
            newAgencyIds.includes(id),
        );
        setAgencyIdsWithAllClientAccess(updatedAllAccess);

        const newViewAgency =
            validAgencies.length > 1
                ? ALL_ASSIGNED_AGENCIES_VALUE
                : validAgencies.length === 1
                  ? validAgencies[0].id
                  : ALL_ASSIGNED_AGENCIES_VALUE;
        setSelectedViewAgency(newViewAgency);

        updateVisibleClients(
            newViewAgency,
            null,
            validAgencies,
            updatedAllAccess,
            updatedByAgency,
        );
    };

    const handleViewAgencyChange = value => {
        const agencyId =
            value === ALL_ASSIGNED_AGENCIES_VALUE
                ? ALL_ASSIGNED_AGENCIES_VALUE
                : Number(value);
        setSelectedViewAgency(agencyId);
        updateVisibleClients(
            agencyId,
            null,
            selectedAgencies,
            agencyIdsWithAllClientAccess,
            clientAccessByAgency,
        );
    };

    const toggleAllClientAccessForAgency = () => {
        if (selectedViewAgency === ALL_ASSIGNED_AGENCIES_VALUE) {
            const allHaveAccess = selectedAgencies.every(a =>
                agencyIdsWithAllClientAccess.includes(a.id),
            );

            if (allHaveAccess) {
                if (preToggleSnapshot) {
                    setAgencyIdsWithAllClientAccess(
                        preToggleSnapshot.allAccessIds,
                    );
                    setClientAccessByAgency(preToggleSnapshot.clientsByAgency);
                    setPreToggleSnapshot(null);
                } else {
                    setAgencyIdsWithAllClientAccess([]);
                }
            } else {
                setPreToggleSnapshot({
                    allAccessIds: [...agencyIdsWithAllClientAccess],
                    clientsByAgency: { ...clientAccessByAgency },
                });
                const newAllAccessIds = selectedAgencies.map(a => a.id);
                setAgencyIdsWithAllClientAccess(newAllAccessIds);
                const updatedByAgency = { ...clientAccessByAgency };
                selectedAgencies.forEach(a => {
                    if (!agencyIdsWithAllClientAccess.includes(a.id)) {
                        const agencyClients = allClientOptions
                            .filter(c => c.agencyId === a.id || a.id === 0)
                            .map(c => ({
                                id: c.id,
                                name: c.name,
                                agency: c.agency,
                            }));
                        updatedByAgency[a.id] = agencyClients;
                    }
                });
                setClientAccessByAgency(updatedByAgency);
            }
            return;
        }
        const agencyId = selectedViewAgency;
        const hasAccess = agencyIdsWithAllClientAccess.includes(agencyId);

        if (hasAccess) {
            setAgencyIdsWithAllClientAccess(prev =>
                prev.filter(id => id !== agencyId),
            );
        } else {
            setAgencyIdsWithAllClientAccess(prev => [...prev, agencyId]);
            const agencyClients = allClientOptions
                .filter(c => c.agencyId === agencyId || agencyId === 0)
                .map(c => ({ id: c.id, name: c.name, agency: c.agency }));
            setClientAccessByAgency(prev => ({
                ...prev,
                [agencyId]: agencyClients,
            }));
        }
    };

    const updateClientAccess = selectedClients => {
        if (selectedViewAgency === ALL_ASSIGNED_AGENCIES_VALUE) return;
        setClientAccessByAgency(prev => ({
            ...prev,
            [selectedViewAgency]: selectedClients,
        }));
    };

    const removeClientBadge = clientToRemove => {
        if (clientToRemove.isBadgeGroup) {
            const agencyId = Number(
                String(clientToRemove.id).replace('all-', ''),
            );
            setAgencyIdsWithAllClientAccess(prev =>
                prev.filter(id => id !== agencyId),
            );
            return;
        }
        if (selectedViewAgency === ALL_ASSIGNED_AGENCIES_VALUE) {
            const updatedByAgency = { ...clientAccessByAgency };
            const agenciesToRemoveAllAccess = [];
            Object.keys(updatedByAgency).forEach(key => {
                const hadClient = updatedByAgency[key].some(
                    c => c.id === clientToRemove.id,
                );
                updatedByAgency[key] = updatedByAgency[key].filter(
                    c => c.id !== clientToRemove.id,
                );
                if (
                    hadClient &&
                    agencyIdsWithAllClientAccess.includes(Number(key))
                ) {
                    agenciesToRemoveAllAccess.push(Number(key));
                }
            });
            setClientAccessByAgency(updatedByAgency);
            if (agenciesToRemoveAllAccess.length > 0) {
                setAgencyIdsWithAllClientAccess(prev =>
                    prev.filter(id => !agenciesToRemoveAllAccess.includes(id)),
                );
            }
        } else {
            if (agencyIdsWithAllClientAccess.includes(selectedViewAgency)) {
                setAgencyIdsWithAllClientAccess(prev =>
                    prev.filter(id => id !== selectedViewAgency),
                );
            }
            setClientAccessByAgency(prev => ({
                ...prev,
                [selectedViewAgency]: (prev[selectedViewAgency] || []).filter(
                    c => c.id !== clientToRemove.id,
                ),
            }));
        }
    };

    const viewAgencyDropdownOptions = useMemo(() => {
        const options = selectedAgencies.map(a => ({
            value: a.id,
            label: a.name,
        }));

        if (selectedAgencies.length > 1) {
            return [
                {
                    value: ALL_ASSIGNED_AGENCIES_VALUE,
                    label: getText('msg_047faedff686'),
                },
                ...options,
            ];
        }

        return options;
    }, [selectedAgencies]);

    const currentViewHasAllAccess = useMemo(() => {
        if (selectedViewAgency === ALL_ASSIGNED_AGENCIES_VALUE) {
            return (
                selectedAgencies.length > 0 &&
                selectedAgencies.every(a =>
                    agencyIdsWithAllClientAccess.includes(a.id),
                )
            );
        }
        return agencyIdsWithAllClientAccess.includes(selectedViewAgency);
    }, [selectedViewAgency, agencyIdsWithAllClientAccess, selectedAgencies]);

    const displayedClientAccess = useMemo(() => {
        if (selectedViewAgency === ALL_ASSIGNED_AGENCIES_VALUE) {
            const allClients = [];
            selectedAgencies.forEach(a => {
                const agencyId = a.id;
                const hasAll = agencyIdsWithAllClientAccess.includes(agencyId);
                if (hasAll) {
                    allClients.push({
                        id: `all-${agencyId}`,
                        name: `All ${a.name} Clients`,
                        isBadgeGroup: true,
                    });
                } else {
                    const clients = clientAccessByAgency[agencyId] || [];
                    clients.forEach(c => {
                        if (
                            !allClients.find(existing => existing.id === c.id)
                        ) {
                            allClients.push(c);
                        }
                    });
                }
            });
            return allClients.sort((a, b) => {
                if (a.isBadgeGroup && !b.isBadgeGroup) return -1;
                if (!a.isBadgeGroup && b.isBadgeGroup) return 1;
                return a.name.localeCompare(b.name);
            });
        }
        const clients = clientAccessByAgency[selectedViewAgency] || [];
        return [...clients].sort((a, b) => a.name.localeCompare(b.name));
    }, [
        selectedViewAgency,
        clientAccessByAgency,
        agencyIdsWithAllClientAccess,
        selectedAgencies,
    ]);

    const handleClose = () => {
        // Users who have the multitenantFlag have no home page for now - the '/' route brings them to the Clients tab
        // Once the Multi Tenant dashboard work is complete, then the '/' route will bring them to the Multi Tenant dashboard (component MultiTenantHomePage.js)
        // See ticket/spec for more info - dev.azure.com/MMADevOps/MMA-SW-WEB-INGAGED/_workitems/edit/43470
        navigate(multitenantFlag ? ROUTES.ROOT : ROUTES.CLIENTS);
    };

    const validateForm = () => {
        // first name is required
        if (firstName === '') {
            Toast.error(getText('msg_6ae3e6ac727a'));
            return false;
        }

        // last name is required
        if (lastName === '') {
            Toast.error(getText('msg_ec032cfad80c'));
            return false;
        }

        // contact type is required
        if (email === '') {
            Toast.error(getText('msg_4da1d591c49e'));
            return false;
        }

        if (
            !isSuperUser &&
            (!securityRole ||
                !securityRole.value ||
                securityRole.value === '' ||
                securityRole.value === 0)
        ) {
            Toast.error(getText('msg_eec3193fdedf'));
            return false;
        }

        if (!selectedAgencies || selectedAgencies.length === 0) {
            Toast.error(getText('msg_fa1f00c848be'));
            return false;
        }

        if (email !== '' && !isValidEmailAddress(email)) {
            Toast.error(getText('msg_fe7db1d225b1'));
            return false;
        }

        if (phone && !isValidPhoneNumber(phone)) {
            Toast.error(getText('msg_f1d3d4393354'));
            return false;
        }

        const hasInsufficientClients = selectedAgencies.some(a => {
            const hasAllAccess = agencyIdsWithAllClientAccess.includes(a.id);
            const clients = clientAccessByAgency[a.id] || [];
            return !hasAllAccess && clients.length === 0;
        });

        if (hasInsufficientClients) {
            Toast.error(getText('msg_d2ba82dff643'));
            return false;
        }

        if (
            accountAccessEndDate &&
            accountAccessEndDate !== initialAccountAccessEndDate
        ) {
            const today = new Date();
            const currentDate = new Date(
                today.getFullYear(),
                today.getMonth(),
                today.getDate(),
            );
            const comparisonDate = parseISO(accountAccessEndDate);

            if (comparisonDate < currentDate) {
                Toast.error(getText('msg_927175d081b3'));
                return false;
            }
        }

        return true;
    };

    const handleSave = () => {
        if (!validateForm()) return;

        let passwordIsValid = false;

        if (isNewUserAccount) {
            setPassword(null);
            passwordIsValid = true;
        } else passwordIsValid = handlePasswordVerification();

        if (passwordIsValid) {
            setIsSaving(true);

            const agenciesPayload = selectedAgencies.map(a => {
                const existingId = existingUserAgencies.includes(a.id)
                    ? a.id
                    : 0;
                return {
                    id: existingId,
                    tenantUserId: Number(userAccountId) || 0,
                    agencyId: a.id,
                    hasAllClientAccess: agencyIdsWithAllClientAccess.includes(
                        a.id,
                    ),
                };
            });

            const clientAccessIds = [];
            selectedAgencies.forEach(a => {
                if (!agencyIdsWithAllClientAccess.includes(a.id)) {
                    const clients = clientAccessByAgency[a.id] || [];
                    clients.forEach(c => {
                        if (!clientAccessIds.includes(c.id)) {
                            clientAccessIds.push(c.id);
                        }
                    });
                }
            });

            const data = {
                id: userAccountId,
                tenantUserStatusId: isAccountEnabled
                    ? TenantUserStatusEnum.Active
                    : TenantUserStatusEnum.Disabled,
                firstName: firstName || null,
                lastName: lastName || null,
                phoneNumber: phone || null,
                emailAddress: email || null,
                isSuperUser,
                securityRoleId: isSuperUser ? null : securityRole.value || null,
                agencies: agenciesPayload,
                clients: clientAccessIds,
                accountAccessEndDate: accountAccessEndDate,
            };

            if (password) {
                data.password = password;
                data.tenantUserStatusId = TenantUserStatusEnum.Active;
            }

            if (isNewUserAccount) {
                data.id = 0;

                createAdminUser(data)
                    .then(async response => {
                        if (sendRegistrationOnCreate) {
                            const createdUserId =
                                response?.id ||
                                response?.tenantUserId ||
                                response?.userId;

                            if (createdUserId) {
                                try {
                                    await initiateMultitenantUserOnboarding(
                                        createdUserId,
                                        email,
                                        phone,
                                    );
                                } catch (error) {
                                    console.error(
                                        'Failed to initiate onboarding:',
                                        error,
                                    );
                                    Toast.error(getText('msg_efc695333371'));
                                }
                            }
                        }

                        Toast.success(
                            "User with email '" +
                                email +
                                "' was created successfully.",
                        );
                        navigate(ROUTES.USER_ACCOUNTS);
                    })
                    .catch(error => {
                        if (error?.response?.data?.detail) {
                            Toast.error(error?.response?.data?.detail);
                        } else Toast.error(getText('msg_3846eac0186d'));
                    })
                    .finally(() => {
                        setIsSaving(false);
                    });
            } else {
                updateAdminUser(data)
                    .then(() => {
                        Toast.success(
                            "User with email '" +
                                email +
                                "' was updated successfully.",
                        );

                        if (password) {
                            setUserAccount(prev => ({
                                ...prev,
                                tenantUserStatusId: TenantUserStatusEnum.Active,
                            }));
                            setPassword('');
                            setPassword1('');
                        }
                    })
                    .catch(error => {
                        console.error(error);
                        if (error?.response?.data?.detail)
                            Toast.error(error?.response?.data?.detail);
                        else Toast.error(getText('msg_54aec888db53'));
                    })
                    .finally(() => {
                        setIsSaving(false);
                    });
            }
        }
    };

    const getStatusString = status => {
        switch (status) {
            case 1:
                return 'Pending';
            case 2:
                return 'Not Started';
            case 3:
                return 'Active';
            case 4:
                return 'Inactive';
            case 5:
                return 'Disabled';
            case 6:
                return 'Locked';
            default:
                return 'Undefined';
        }
    };

    const getStatusClass = status => {
        switch (status) {
            case 1:
                return 'ing-badge-pending';
            case 2:
                return 'ing-badge-inv-sent';
            case 3:
                return 'ing-badge-active';
            case 4:
                return 'ing-badge-inactive';
            case 5:
                return 'ing-badge-disabled';
            case 6:
                return 'ing-badge-error';
            default:
                return 'ing-badge-inactive';
        }
    };

    const onToggleAccountEnabled = () => {
        showModal({
            type: ModalTypes.ACTION,
            title: isAccountEnabled
                ? 'This account is currently enabled'
                : 'This account is currently disabled',
            text: isAccountEnabled
                ? 'Would you like to disable it?'
                : 'Would you like to enable it?',
            onYes: () => toggleUserStatus(),
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: isAccountEnabled ? 'Disable' : 'Enable',
        });
    };

    const toggleUserStatus = async () => {
        setIsToggling(true);
        const newAccountStatus = !isAccountEnabled;
        const newStatusId = newAccountStatus
            ? TenantUserStatusEnum.Active
            : TenantUserStatusEnum.Disabled;

        try {
            const storedAgencyIds = userAccount.agencies || [];
            const storedAllClientAccessIds =
                userAccount.agencyIdsWithAllClientAccess || [];

            const agenciesPayload = storedAgencyIds.map(agencyId => ({
                id: agencyId,
                tenantUserId: Number(userAccountId) || 0,
                agencyId,
                hasAllClientAccess: storedAllClientAccessIds.includes(agencyId),
            }));

            const data = {
                id: userAccount.id,
                tenantUserStatusId: newStatusId,
                firstName,
                lastName,
                phoneNumber: phone || null,
                emailAddress: email,
                isSuperUser,
                securityRoleId: isSuperUser
                    ? null
                    : securityRole?.value || null,
                agencies: agenciesPayload,
                clients: userAccount.clients || [],
                accountAccessEndDate,
            };

            await updateAdminUser(data);

            setIsAccountEnabled(newAccountStatus);
            setUserAccount(prev => ({
                ...prev,
                tenantUserStatusId: newStatusId,
            }));

            Toast.success(
                `Account has been ${newAccountStatus ? 'enabled' : 'disabled'} successfully!`,
            );
        } catch (error) {
            console.error(error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(getText('msg_54aec888db53'));
            }
        } finally {
            setIsToggling(false);
        }
    };

    const handleDelete = () => {
        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_40e190999ffc'),
            text: getText('msg_d9ec33878e60'),
            onYes: () => deleteUserAccount(),
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(LogEventType.DeleteClicked, `User delete clicked`);
    };

    const deleteUserAccount = () => {
        deleteAdminUserById(userAccountId).then(response => {
            Toast.success(
                "User with email '" + email + "' was deleted successfully.",
            );
            navigate(ROUTES.USER_ACCOUNTS);
        });
    };

    const handleResetAccount = () => {
        if (!canEditForm) return;

        showModal({
            type: ModalTypes.ACTION,
            title: getText('msg_8cc227914db7'),
            subtitle: getText('msg_80197bf075cd'),
            text: getText('msg_55511b9fcbd1'),
            onYes: async () => {
                setIsResetting(true);
                try {
                    await resetUser(
                        0,
                        userAccount?.externalId || userAccountId,
                    );
                    Toast.success(getText('msg_2a43ba2ac1c2'));
                    loadUserAccountDetails();
                } catch (error) {
                    console.error('Error resetting account:', error);
                    if (error?.response?.data?.detail) {
                        Toast.error(error?.response?.data?.detail);
                    } else {
                        Toast.error(getText('msg_bc9803eb2840'));
                    }
                } finally {
                    setIsResetting(false);
                }
            },
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_8cc227914db7'),
        });
    };

    const handleToggleLockAccount = () => {
        if (!canEditForm) return;

        const currentStatusType = getUserStatusType(
            userAccount.tenantUserStatusId,
        );
        const isCurrentlyLocked = currentStatusType === 'Locked';
        const action = isCurrentlyLocked ? 'unlock' : 'lock';

        showModal({
            type: ModalTypes.ACTION,
            title: `${isCurrentlyLocked ? 'Unlock' : 'Lock'} Account`,
            subtitle: `Are you sure you want to ${action} this account?`,
            text: isCurrentlyLocked
                ? 'This will allow the user to access their account again.'
                : 'This will prevent the user from accessing their account.',
            onYes: async () => {
                setIsLocking(true);
                try {
                    const newStatusId = isCurrentlyLocked
                        ? TenantUserStatusEnum.Active
                        : TenantUserStatusEnum.Locked;

                    const data = {
                        ...userAccount,
                        tenantUserStatusId: newStatusId,
                    };

                    await updateAdminUser(data);

                    setUserAccount(prev => ({
                        ...prev,
                        tenantUserStatusId: newStatusId,
                    }));

                    Toast.success(
                        `Account has been ${isCurrentlyLocked ? 'unlocked' : 'locked'} successfully!`,
                    );
                } catch (error) {
                    console.error(`Error ${action}ing account:`, error);
                    if (error?.response?.data?.detail) {
                        Toast.error(error?.response?.data?.detail);
                    } else {
                        Toast.error(`Unable to ${action} account`);
                    }
                } finally {
                    setIsLocking(false);
                }
            },
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: `${isCurrentlyLocked ? 'Unlock' : 'Lock'}`,
        });
    };

    const validatePassword = password => {
        setPasswordRequirements({
            length:
                password.length >=
                    companyConfig.application?.password_min_length &&
                password.length <=
                    companyConfig.application?.password_max_length,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
            repeats: !/(.)\1{3}/.test(password),
            sequential: !hasSequentialChars(password),
            patterns: !/(.{1,2})\1\1/.test(password),
        });
    };

    const handlePasswordChange = event => {
        const value = event.target.value;
        setPassword1(value);

        if (value) {
            validatePassword(value);
        }

        if (password && value !== password) {
            setPasswordError('Passwords do not match');
        } else {
            setPasswordError('');
        }
    };

    const handleConfirmPasswordChange = event => {
        const value = event.target.value;
        setPassword(value);
        if (value && password1 && value !== password1) {
            setPasswordError('Passwords do not match');
        } else {
            setPasswordError('');
        }
    };

    const renderPasswordRequirement = (requirement, text) => {
        const isMet = passwordRequirements[requirement];
        return (
            <small
                className={`password-requirement d-block w-100 ${
                    !password1
                        ? 'text-muted'
                        : isMet
                          ? 'text-success'
                          : 'text-danger'
                }`}
            >
                {password1 && (
                    <i className={`bi ${isMet ? 'bi-check' : 'bi-x'} me-2`}></i>
                )}
                {text}
            </small>
        );
    };

    const handlePasswordVerification = () => {
        if (password === '') {
            return true;
        }

        if (password1 !== password) {
            Toast.error(getText('msg_f7c3cd475924'));
            return false;
        }

        const requirements = passwordRequirements;
        const isValidPassword =
            requirements.length &&
            requirements.uppercase &&
            requirements.lowercase &&
            requirements.number &&
            requirements.special &&
            requirements.repeats &&
            requirements.sequential &&
            requirements.patterns;

        if (!isValidPassword) {
            const missingRequirements = [];
            if (!requirements.length)
                missingRequirements.push(
                    companyConfig.application?.password_min_length +
                        '-' +
                        companyConfig.application?.password_max_length +
                        ' ' +
                        'characters',
                );
            if (!requirements.uppercase)
                missingRequirements.push('1 uppercase letter');
            if (!requirements.lowercase)
                missingRequirements.push('1 lowercase letter');
            if (!requirements.number) missingRequirements.push('1 number');
            if (!requirements.special)
                missingRequirements.push('1 special character');
            if (!requirements.repeats)
                missingRequirements.push(
                    'no more than 3 consecutive repeating characters',
                );
            if (!requirements.sequential)
                missingRequirements.push(
                    'no more than 3 sequential characters',
                );
            if (!requirements.patterns)
                missingRequirements.push('no repeating patterns');

            Toast.error(
                `Password must contain: ${missingRequirements.join(', ')}`,
            );
            return false;
        }

        return true;
    };

    const handleInitiateOnboarding = async () => {
        try {
            await initiateMultitenantUserOnboarding(
                userAccountId,
                email,
                phone,
            );
            Toast.success(getText('msg_4598cd4a9f73'));
        } catch (error) {
            console.error('Failed to initiate onboarding:', error);
            Toast.error(getText('msg_efc695333371'));
        }
    };

    const onInitiateOnboarding = () => {
        const message = `The User status is ${getStatusString(
            userAccount.tenantUserStatusId,
        )}`;
        showModal({
            type: ModalTypes.ACTION,
            title: message,
            subtitle: getText('msg_5aaca40741c3'),
            text: '',
            onYes: handleInitiateOnboarding,
            onNo: () => {},
            noButtonText: getText('msg_816c52fd2bdd'),
            yesButtonText: getText('msg_be4e8e2d0338'),
        });
        return;
    };

    const onResetPassword = () => {
        showModal({
            type: ModalTypes.ACTION,
            title: '',
            subtitle: getText('msg_fb6e5ab4e23b'),
            text: '',
            onYes: handleInitiatePasswordReset,
            onNo: () => {},
            noButtonText: getText('msg_816c52fd2bdd'),
            yesButtonText: getText('msg_be4e8e2d0338'),
        });
    };

    const handleInitiatePasswordReset = async () => {
        try {
            let emailAddress = email;

            if (!emailAddress) {
                Toast.error(getText('msg_ed34efd4e3f2'));
                return;
            }
            if (`${passwordResetType}`.toLowerCase().includes('sms')) {
                Toast.error(getText('msg_2dfdd28cbb11'));
                return;
            } else {
                await sendForgotPasswordEmail(null, emailAddress);
            }

            Toast.success(getText('msg_3bcfd52dc4e0'));
        } catch (error) {
            console.error('Unable to request password reset', error);
            if (error?.response?.data?.detail)
                Toast.error(error?.response?.data?.detail);
            else Toast.error(getText('msg_b6ba53994a96'));
        }
    };

    const getPreferredDeliveryType = notificationMethodId => {
        if (
            notificationMethodId === NOTIFICATION_METHOD.primaryPhone ||
            notificationMethodId === NOTIFICATION_METHOD.optionalPhone
        )
            return 'sms';

        // Treat email + anything unknown as email (safe default)
        return 'email';
    };
    const onPasswordResetTypeChange = selectedOption => {
        setPasswordResetType(getDeliveryTypeFromOption(selectedOption));
    };

    const onRegistrationTypeChange = selectedOption => {
        setRegistrationType(getDeliveryTypeFromOption(selectedOption));
    };
    const getDeliveryTypeFromOption = selected => {
        const raw =
            typeof selected === 'string' || typeof selected === 'number'
                ? String(selected).toLowerCase()
                : '';

        if (raw === '1' || raw.includes('sms')) return 'sms';
        return 'email';
    };

    const getDeliveryTypeLabel = deliveryType => {
        if (`${deliveryType}`.toLowerCase().includes('sms')) {
            return 'SMS';
        }

        return 'Email';
    };

    const handlePasswordFocus = event => {
        setShowPasswordRequirements(true);
        setTimeout(() => {
            event.target.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
            });
        }, 100);
    };

    const handlePasswordBlur = () => {
        if (password1 && password1.trim() !== '') return;
        setShowPasswordRequirements(false);
    };

    const getUserStatusType = statusId => {
        if (statusId === 0 || statusId === TenantUserStatusEnum.NotStarted)
            return 'NotStarted';
        if (
            statusId === 1 ||
            statusId === TenantUserStatusEnum.InvitationPending
        )
            return 'InvitationPending';
        if (statusId === 2 || statusId === TenantUserStatusEnum.Pending)
            return 'Pending';
        if (statusId === 3 || statusId === TenantUserStatusEnum.Active)
            return 'Active';
        if (statusId === 4 || statusId === TenantUserStatusEnum.InActive)
            return 'InActive';
        if (statusId === 5 || statusId === TenantUserStatusEnum.Disabled)
            return 'Disabled';
        if (statusId === 6 || statusId === TenantUserStatusEnum.Locked)
            return 'Locked';
        return 'Unknown';
    };

    const AccountStatusManager = () => {
        if (isNewUserAccount) return null;

        const currentStatusType = getUserStatusType(
            userAccount.tenantUserStatusId,
        );

        const renderActionButtons = () => {
            const isLocked = currentStatusType === 'Locked';

            return (
                <div
                    className="d-flex align-items-center"
                    style={{ paddingTop: '32px', paddingBottom: '32px' }}
                >
                    <div className="form-switch me-3">
                        <input
                            className="form-check-input custom-switch-input ml-3"
                            type="checkbox"
                            checked={isAccountEnabled}
                            disabled={!canEditForm || isToggling}
                            name="accountEnabled"
                            onChange={onToggleAccountEnabled}
                        />
                        <label
                            onClick={
                                !canEditForm || isToggling
                                    ? null
                                    : onToggleAccountEnabled
                            }
                            className="form-check-label custom-switch-label px-2 text-nowrap"
                            htmlFor="accountEnabled"
                        >
                            {isToggling ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    <span
                                        style={{
                                            color: '#82808E',
                                            fontFamily: 'Inter',
                                            fontSize: '14px',
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            lineHeight: '20px',
                                        }}
                                    >
                                        {isAccountEnabled
                                            ? 'Disabling...'
                                            : 'Enabling...'}
                                    </span>
                                </>
                            ) : (
                                <span
                                    style={{
                                        color: '#82808E',
                                        fontFamily: 'Inter',
                                        fontSize: '14px',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        lineHeight: '20px',
                                    }}
                                >
                                    {getText('msg_18a83b21ac9d')}
                                </span>
                            )}
                        </label>
                    </div>

                    {canEditForm ||
                        (canResetPassword && (
                            <>
                                {[
                                    'Active',
                                    'InActive',
                                    'Pending',
                                    'Disabled',
                                    'Locked',
                                    'NotStarted',
                                ].includes(currentStatusType) && (
                                    <p
                                        type="button"
                                        onClick={
                                            isResetting
                                                ? null
                                                : handleResetAccount
                                        }
                                        className={`ing-text-buttons d-inline mx-3 ${
                                            isResetting ? 'disabled' : ''
                                        }`}
                                        disabled={isResetting}
                                    >
                                        {isResetting ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                {getText('msg_ee41f75047a1')}
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-arrow-clockwise me-2" />
                                                {getText('msg_8cc227914db7')}
                                            </>
                                        )}
                                    </p>
                                )}

                                {isLocked && (
                                    <p
                                        type="button"
                                        onClick={
                                            isLocking
                                                ? null
                                                : handleToggleLockAccount
                                        }
                                        className={`ing-text-buttons d-inline mx-3 ${
                                            isLocking ? 'disabled' : ''
                                        }`}
                                        disabled={isLocking}
                                    >
                                        {isLocking ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                {getText('msg_0970fd2831e0')}
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-unlock me-2" />
                                                {getText('msg_739f0f24eb91')}
                                            </>
                                        )}
                                    </p>
                                )}
                            </>
                        ))}
                </div>
            );
        };

        return (
            <div className="account-status-manager">
                {renderActionButtons()}
            </div>
        );
    };

    const [communicationPurpose, setCommunicationPurpose] = useState('');
    // const [communicationType, setCommunicationType] = useState('');

    const onCommunicationPurposeChange = value => {
        setCommunicationPurpose(value); // 'registration' | 'passwordReset'
    };
    // const onCommunicationTypeChange = value => {
    //   setCommunicationType(value); // 'sms' | 'email'
    // };

    // const handleSendCommunication = (e) => {
    // e.preventDefault();
    // if (!communicationPurpose || !communicationType) {
    //     // Optionally show validation error here
    //     return;
    // }

    // if (communicationPurpose === 'registration') {
    //     // Call your existing registration send handler, passing communicationType if needed
    //     onInitiateOnboarding(communicationType);
    // } else if (communicationPurpose === 'passwordReset') {
    //     // Call your existing password reset send handler, passing communicationType if needed
    //     onResetPassword(communicationType);
    // }
    // };

    const handleSendCommunication = e => {
        e.preventDefault();

        if (!communicationPurpose) {
            Toast.error(getText('msg_cf9be76cdcc3'));
            return;
        }

        const deliveryType = getPreferredDeliveryType(
            userAccount?.notificationPreferredMethodId,
        );

        if (communicationPurpose === 'registration') {
            setRegistrationType(deliveryType);
            onInitiateOnboarding(); // will open modal + send onboarding
            return;
        }

        if (communicationPurpose === 'passwordReset') {
            setPasswordResetType(deliveryType);
            onResetPassword(); // will open modal; if sms => "not configured"
            return;
        }
    };

    // Add this helper function inside UserAccountView component
    const getCommunicationPreference = notificationMethodId => {
        if (notificationMethodId === NOTIFICATION_METHOD.primaryEmail)
            return 'Email';
        if (notificationMethodId === NOTIFICATION_METHOD.optionalEmail)
            return 'Email';
        if (notificationMethodId === NOTIFICATION_METHOD.primaryPhone)
            return 'SMS';
        if (notificationMethodId === NOTIFICATION_METHOD.optionalPhone)
            return 'SMS';
        if (notificationMethodId === NOTIFICATION_METHOD.pushNotification)
            return 'Push Notification';
        return '-';
    };

    return (
        <div ref={topRef} className={`page-content`}>
            {isUnableToLoad || isUnauthorized ? (
                <>
                    <AlertBox
                        title={`${
                            isUnauthorized
                                ? 'You do not have permission to access this page.'
                                : 'Unable to Load User Account'
                        }`}
                        description={`${
                            isUnauthorized
                                ? 'If you believe this is an error, please reach out to an administrator on your team'
                                : 'Please try again later'
                        }`}
                    />
                </>
            ) : (
                <div className="card">
                    <div className="card-body px-0 py-4 b">
                        <div className="d-flex justify-content-between align-items-center pb-3">
                            <div className={'float-start d-flex'}>
                                <p className="page-title card-title ms-4 mb-0">
                                    {isNewUserAccount
                                        ? 'Add New User'
                                        : `${firstName} ${lastName}`}
                                </p>
                                {!isNewUserAccount && (
                                    <button
                                        className={`ms-4 ${getStatusClass(
                                            userAccount.tenantUserStatusId,
                                        )}`}
                                    >
                                        {getStatusString(
                                            userAccount.tenantUserStatusId,
                                        )}
                                    </button>
                                )}
                            </div>
                            <div className="float-end align-items-center me-4">
                                {canEditForm && (
                                    <>
                                        {!isNewUserAccount && (
                                            <p
                                                type="button"
                                                onClick={handleDelete}
                                                className="ing-text-buttons d-inline me-3"
                                            >
                                                <i className="bi bi-trash me-2" />
                                                {getText('msg_f6fdbe48dc54')}
                                            </p>
                                        )}
                                        <p
                                            type="button"
                                            onClick={handleSave}
                                            className="ing-text-buttons d-inline mx-3"
                                            disabled={
                                                isSaving ||
                                                (!isAccountEnabled &&
                                                    !(
                                                        password1 &&
                                                        password1.trim() !== ''
                                                    ))
                                            }
                                        >
                                            {isSaving ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    ></span>
                                                    {getText(
                                                        'msg_ae7e887517b0',
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-check-lg me-2" />
                                                    {getText(
                                                        'msg_efc007a393f6',
                                                    )}
                                                </>
                                            )}
                                        </p>
                                    </>
                                )}
                                <p
                                    type="button"
                                    onClick={handleClose}
                                    className="ing-text-buttons d-inline ms-3"
                                >
                                    <i className="bi bi-x me-2" />
                                    {getText('msg_bbfa773e5a63')}
                                </p>
                            </div>
                        </div>
                        {isLoading && userAccount ? (
                            <Spinner width={50} className="loadingSpinner" />
                        ) : (
                            <>
                                <div className="container user-account-container user-account-main-container">
                                    {/* ===== MAIN GRID: form columns left, profile card right ===== */}
                                    <div
                                        className={`user-account-layout ${
                                            isNewUserAccount
                                                ? 'user-account-layout--single'
                                                : ''
                                        }`}
                                    >
                                        {/* Left column: personal details + security + client access */}
                                        <div className="user-account-left-column">
                                            {/* Personal Details */}
                                            <div className="account-status-wrapper">
                                                {!isAccountEnabled && (
                                                    <p
                                                        style={{
                                                            color: 'gray',
                                                        }}
                                                    >
                                                        {getText(
                                                            'msg_66aa8e9b0915',
                                                        )}
                                                    </p>
                                                )}
                                                {!isNewUserAccount && (
                                                    <AccountStatusManager />
                                                )}
                                            </div>
                                            <div className="personal-details">
                                                <div className="form-container">
                                                    <form>
                                                        <div className="form-row">
                                                            <div className="form-group">
                                                                <label htmlFor="firstname">
                                                                    {getText(
                                                                        'msg_b6ea992aab46',
                                                                    )}
                                                                    {isNewUserAccount && (
                                                                        <span className="text-danger">
                                                                            {' '}
                                                                            *
                                                                        </span>
                                                                    )}
                                                                </label>
                                                                <input
                                                                    id="firstname"
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={
                                                                        firstName
                                                                    }
                                                                    onChange={e =>
                                                                        setFirstName(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        )
                                                                    }
                                                                    name="firstname"
                                                                    placeholder={getText(
                                                                        'msg_0f6fa8fa8048',
                                                                    )}
                                                                    required
                                                                    disabled={
                                                                        !isAccountEnabled
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="form-group">
                                                                <label htmlFor="lastname">
                                                                    {getText(
                                                                        'msg_863cb39fbe7d',
                                                                    )}
                                                                    {isNewUserAccount && (
                                                                        <span className="text-danger">
                                                                            {' '}
                                                                            *
                                                                        </span>
                                                                    )}
                                                                </label>
                                                                <input
                                                                    id="lastname"
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={
                                                                        lastName
                                                                    }
                                                                    onChange={e =>
                                                                        setLastName(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        )
                                                                    }
                                                                    name="lastname"
                                                                    placeholder={getText(
                                                                        'msg_9efa5a4810e3',
                                                                    )}
                                                                    required
                                                                    disabled={
                                                                        !isAccountEnabled
                                                                    }
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="form-row">
                                                            <div
                                                                className="form-group"
                                                                style={{
                                                                    marginTop:
                                                                        '25px',
                                                                }}
                                                            >
                                                                <label htmlFor="phone">
                                                                    <i
                                                                        className="bi bi-telephone"
                                                                        style={{
                                                                            marginRight:
                                                                                '10px',
                                                                        }}
                                                                    ></i>
                                                                    {getText(
                                                                        'msg_77064d526523',
                                                                    )}
                                                                </label>
                                                                <PhoneInput
                                                                    id="phone"
                                                                    className="form-control"
                                                                    value={
                                                                        phone
                                                                    }
                                                                    onChange={e =>
                                                                        setPhone(
                                                                            e,
                                                                        )
                                                                    }
                                                                    name="phone"
                                                                    placeholder={getText(
                                                                        'msg_e6b9d1a95e67',
                                                                    )}
                                                                    country="US"
                                                                    disabled={
                                                                        !isAccountEnabled
                                                                    }
                                                                    style={{
                                                                        marginBottom:
                                                                            '8px',
                                                                    }}
                                                                />

                                                                <small className="custom-small-text">
                                                                    {getText(
                                                                        'msg_776a2c441364',
                                                                    )}
                                                                    <span className="text-danger">
                                                                        *
                                                                    </span>
                                                                </small>
                                                            </div>
                                                            <div className="form-group">
                                                                <label htmlFor="email">
                                                                    <i
                                                                        className="bi bi-envelope"
                                                                        style={{
                                                                            marginRight:
                                                                                '10px',
                                                                        }}
                                                                    ></i>
                                                                    {getText(
                                                                        'msg_84add5b29527',
                                                                    )}
                                                                </label>
                                                                <input
                                                                    id="email"
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={
                                                                        email
                                                                    }
                                                                    onChange={e =>
                                                                        setEmail(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        )
                                                                    }
                                                                    name="email"
                                                                    placeholder={getText(
                                                                        'msg_3ce7dd24ce64',
                                                                    )}
                                                                    disabled={
                                                                        !isAccountEnabled
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    </form>
                                                </div>
                                            </div>
                                            <hr className="user-account-section-divider" />
                                            {/* Security section — flows directly below personal details */}
                                            <div className="security security-section-spacing">
                                                <form>
                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <label
                                                                    htmlFor="attributes"
                                                                    style={{
                                                                        marginBottom: 0,
                                                                    }}
                                                                >
                                                                    {getText(
                                                                        'msg_e65f57bfd8d7',
                                                                    )}{' '}
                                                                    <span className="text-danger">
                                                                        {' '}
                                                                        *
                                                                    </span>
                                                                </label>
                                                                <div
                                                                    className="form-switch d-flex align-items-center gap-2"
                                                                    style={{
                                                                        paddingLeft:
                                                                            '2.5em',
                                                                        marginBottom: 0,
                                                                    }}
                                                                >
                                                                    <input
                                                                        onClick={() =>
                                                                            setIsSuperUser(
                                                                                !isSuperUser,
                                                                            )
                                                                        }
                                                                        className="form-check-input custom-switch-input"
                                                                        type="checkbox"
                                                                        checked={
                                                                            isSuperUser
                                                                        }
                                                                        id="issuperuser"
                                                                        disabled={
                                                                            !isAccountEnabled
                                                                        }
                                                                    />
                                                                    <label
                                                                        className="fw-medium"
                                                                        style={{
                                                                            whiteSpace:
                                                                                'nowrap',
                                                                            marginBottom: 0,
                                                                        }}
                                                                        htmlFor="issuperuser"
                                                                    >
                                                                        {getText(
                                                                            'msg_7792ee74c74d',
                                                                        )}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <Dropdown
                                                                defaultValue={
                                                                    securityRole.label ||
                                                                    'Select Option'
                                                                }
                                                                options={
                                                                    securityRolesOptions
                                                                }
                                                                onChange={
                                                                    handleSecurityRolesDropdown
                                                                }
                                                                disabled={
                                                                    !isAccountEnabled ||
                                                                    isSuperUser
                                                                }
                                                            />
                                                        </div>
                                                        <AgencyMultiSelect
                                                            selectedAgencies={
                                                                selectedAgencies
                                                            }
                                                            onAgenciesChange={
                                                                handleAssignedAgenciesChange
                                                            }
                                                            isRequired
                                                            disabled={
                                                                !isAccountEnabled
                                                            }
                                                        />
                                                    </div>
                                                    <div className="form-row">
                                                        <div
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <div className="form-row col-lg-12 d-flex mb-2 mt-4">
                                                                <label htmlFor="attributes">
                                                                    {getText(
                                                                        'msg_c91ea7db6d70',
                                                                    )}
                                                                    <span className="text-danger">
                                                                        {' '}
                                                                        *
                                                                    </span>
                                                                </label>
                                                            </div>
                                                            <div
                                                                style={{
                                                                    marginLeft:
                                                                        '5px',
                                                                }}
                                                            >
                                                                <p
                                                                    className="text-muted mb-3"
                                                                    style={{
                                                                        fontSize:
                                                                            '13px',
                                                                    }}
                                                                >
                                                                    {getText(
                                                                        'msg_f76ee6bd0b69',
                                                                    )}
                                                                </p>
                                                            </div>

                                                            <div className="form-row">
                                                                <div className="form-group">
                                                                    <label htmlFor="viewAgency">
                                                                        {getText(
                                                                            'msg_5c47e26c6ad2',
                                                                        )}
                                                                    </label>
                                                                    <Dropdown
                                                                        defaultValue={
                                                                            viewAgencyDropdownOptions.find(
                                                                                o =>
                                                                                    o.value ===
                                                                                    selectedViewAgency,
                                                                            )
                                                                                ?.label ||
                                                                            'Select'
                                                                        }
                                                                        options={
                                                                            viewAgencyDropdownOptions
                                                                        }
                                                                        onChange={
                                                                            handleViewAgencyChange
                                                                        }
                                                                        disabled={
                                                                            !isAccountEnabled ||
                                                                            selectedAgencies.length ===
                                                                                0
                                                                        }
                                                                    />
                                                                </div>
                                                                <div className="form-group">
                                                                    <label htmlFor="clients">
                                                                        {getText(
                                                                            'msg_28e22fe3dde5',
                                                                        )}
                                                                    </label>
                                                                    <SearchableDropdown
                                                                        options={
                                                                            clientOptions
                                                                        }
                                                                        optionIDField="id"
                                                                        savedOptions={
                                                                            currentViewHasAllAccess ||
                                                                            selectedViewAgency ===
                                                                                ALL_ASSIGNED_AGENCIES_VALUE
                                                                                ? []
                                                                                : clientAccessByAgency[
                                                                                      selectedViewAgency
                                                                                  ] ||
                                                                                  []
                                                                        }
                                                                        savedOptionIDField="id"
                                                                        updateOptions={options =>
                                                                            updateClientAccess(
                                                                                options,
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            !isAccountEnabled ||
                                                                            currentViewHasAllAccess ||
                                                                            selectedViewAgency ===
                                                                                ALL_ASSIGNED_AGENCIES_VALUE
                                                                        }
                                                                        showTags={
                                                                            false
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="d-flex justify-content-end mb-2 mt-2">
                                                                <div
                                                                    className={`form-switch`}
                                                                >
                                                                    <input
                                                                        onClick={
                                                                            toggleAllClientAccessForAgency
                                                                        }
                                                                        className="form-check-input custom-switch-input"
                                                                        type="checkbox"
                                                                        checked={
                                                                            currentViewHasAllAccess
                                                                        }
                                                                        id="allclients"
                                                                        disabled={
                                                                            !isAccountEnabled ||
                                                                            selectedAgencies.length ===
                                                                                0
                                                                        }
                                                                    />
                                                                    <label
                                                                        className="fw-medium"
                                                                        style={{
                                                                            whiteSpace:
                                                                                'nowrap',
                                                                        }}
                                                                        htmlFor="allclients"
                                                                    >
                                                                        {getText(
                                                                            'msg_34fd4ec957ed',
                                                                        )}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <div className="client-badges-area">
                                                                {displayedClientAccess.map(
                                                                    client => (
                                                                        <span
                                                                            key={
                                                                                client.id
                                                                            }
                                                                            className="badge-base badge-population-group"
                                                                        >
                                                                            {isAccountEnabled && (
                                                                                <i
                                                                                    onClick={() =>
                                                                                        removeClientBadge(
                                                                                            client,
                                                                                        )
                                                                                    }
                                                                                    className="bi bi-x-lg"
                                                                                    style={{
                                                                                        marginRight:
                                                                                            '8px',
                                                                                        cursor: 'pointer',
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            {
                                                                                client.name
                                                                            }
                                                                        </span>
                                                                    ),
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                            <hr className="user-account-section-divider section-divider-spacing" />
                                        </div>

                                        {/* Right column: profile card (only for existing users) */}
                                        {!isNewUserAccount && (
                                            <div className="user-account-right-column user-account-profile-card">
                                                <div
                                                    style={{
                                                        width: '220px',
                                                        height: '220px',
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            profilePicture
                                                                ? 'transparent'
                                                                : '#b0b0b0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        fontSize: '36px',
                                                        fontWeight: '700',
                                                        color: 'white',
                                                        fontFamily:
                                                            'Inter, sans-serif',
                                                        userSelect: 'none',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {profilePicture ? (
                                                        <img
                                                            src={profilePicture}
                                                            alt={getText(
                                                                'msg_ff4fc0276e96',
                                                            )}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                borderRadius:
                                                                    '50%',
                                                                objectFit:
                                                                    'cover',
                                                            }}
                                                        />
                                                    ) : (
                                                        `${(firstName?.[0] || '').toUpperCase()}${(lastName?.[0] || '').toUpperCase()}`
                                                    )}
                                                </div>
                                                <div
                                                    style={{
                                                        color: '#2F2B43',
                                                        textAlign: 'center',
                                                        fontFamily: 'Inter',
                                                        fontSize: '20px',
                                                        fontStyle: 'normal',
                                                        fontWeight: 700,
                                                        lineHeight: '28px',
                                                    }}
                                                >
                                                    {firstName} {lastName}
                                                </div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '10px',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            paddingLeft: '37px',
                                                            display: 'flex',
                                                            gap: '24px',
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                flex: 1,
                                                                textAlign:
                                                                    'left',
                                                            }}
                                                        >
                                                            <label
                                                                style={{
                                                                    fontWeight: 600,
                                                                    fontSize:
                                                                        '12px',
                                                                    color: '#82808e',
                                                                    marginBottom:
                                                                        '2px',
                                                                    display:
                                                                        'block',
                                                                }}
                                                            >
                                                                {getText(
                                                                    'msg_6df7d8380ffc',
                                                                )}
                                                            </label>
                                                            <p
                                                                style={{
                                                                    margin: 0,
                                                                    fontWeight: 500,
                                                                    fontSize:
                                                                        '14px',
                                                                }}
                                                            >
                                                                {preferredName ||
                                                                    '-'}
                                                            </p>
                                                        </div>
                                                        <div
                                                            style={{
                                                                flex: 1,
                                                                textAlign:
                                                                    'left',
                                                            }}
                                                        >
                                                            <label
                                                                style={{
                                                                    fontWeight: 600,
                                                                    fontSize:
                                                                        '12px',
                                                                    color: '#82808e',
                                                                    marginBottom:
                                                                        '2px',
                                                                    display:
                                                                        'block',
                                                                }}
                                                            >
                                                                {getText(
                                                                    'msg_8a2abafdb920',
                                                                )}
                                                            </label>
                                                            <p
                                                                style={{
                                                                    margin: 0,
                                                                    fontWeight: 500,
                                                                    fontSize:
                                                                        '14px',
                                                                }}
                                                            >
                                                                {pronouns ||
                                                                    '-'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div
                                                        style={{
                                                            paddingLeft: '37px',
                                                            textAlign: 'left',
                                                        }}
                                                    >
                                                        <label
                                                            style={{
                                                                fontWeight: 600,
                                                                fontSize:
                                                                    '12px',
                                                                color: '#82808e',
                                                                marginBottom:
                                                                    '2px',
                                                                display:
                                                                    'block',
                                                            }}
                                                        >
                                                            {getText(
                                                                'msg_89b86ab0e66f',
                                                            )}
                                                        </label>
                                                        <p
                                                            style={{
                                                                margin: 0,
                                                                fontWeight: 500,
                                                                fontSize:
                                                                    '14px',
                                                            }}
                                                        >
                                                            {language ||
                                                                'Not specified'}
                                                        </p>
                                                    </div>
                                                    <div
                                                        style={{
                                                            paddingLeft: '37px',
                                                            textAlign: 'left',
                                                        }}
                                                    >
                                                        <label
                                                            style={{
                                                                fontWeight: 600,
                                                                fontSize:
                                                                    '12px',
                                                                color: '#82808e',
                                                                marginBottom:
                                                                    '2px',
                                                                display:
                                                                    'block',
                                                            }}
                                                        >
                                                            {getText(
                                                                'msg_88d7dacc35f7',
                                                            )}
                                                        </label>
                                                        <p
                                                            style={{
                                                                margin: 0,
                                                                fontWeight: 500,
                                                                fontSize:
                                                                    '14px',
                                                            }}
                                                        >
                                                            {getCommunicationPreference(
                                                                userAccount.notificationPreferredMethodId,
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* ===== END MAIN GRID ===== */}
                                </div>
                                {!isNewUserAccount ? (
                                    <>
                                        <div className="container user-account-container user-account-access-container">
                                            <div className="account-access">
                                                <form>
                                                    <div className="access-row row account-access-row--wide">
                                                        <div className="form-group col-6">
                                                            <label htmlFor="accountAccessEndDate">
                                                                {getText(
                                                                    'msg_84b147812589',
                                                                )}
                                                                <Tooltip
                                                                    html="Set an optional end date for account access. After this date, the user will no longer be able to access their account."
                                                                    placement="right"
                                                                >
                                                                    <i
                                                                        className="bi bi-info-circle"
                                                                        style={{
                                                                            paddingLeft:
                                                                                '5px',
                                                                            fontSize:
                                                                                '13px',
                                                                            color: 'var(--iNGAGED-Primary-Color)',
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </label>
                                                            <CustomDatePicker
                                                                selectedDate={
                                                                    accountAccessEndDate
                                                                }
                                                                onDateChange={date =>
                                                                    setAccountAccessEndDate(
                                                                        date,
                                                                    )
                                                                }
                                                                disabled={
                                                                    !isAccountEnabled
                                                                }
                                                            />
                                                        </div>
                                                        <div className="col-6"></div>
                                                    </div>

                                                    <div className="access-row row account-access-row--communication">
                                                        <div className="form-group">
                                                            <label htmlFor="sendCommunication">
                                                                {getText(
                                                                    'msg_f0ce7ab678f1',
                                                                )}{' '}
                                                            </label>
                                                            <div className="send-group d-flex align-items-center">
                                                                <div className="dropdown-width me-3">
                                                                    <Dropdown
                                                                        id="sendCommunication"
                                                                        defaultValue="Select Communication"
                                                                        options={[
                                                                            {
                                                                                value: 'registration',
                                                                                label: getText(
                                                                                    'msg_b233e775d63b',
                                                                                ),
                                                                            },
                                                                            {
                                                                                value: 'passwordReset',
                                                                                label: getText(
                                                                                    'msg_79167df1dd0b',
                                                                                ),
                                                                            },
                                                                        ]}
                                                                        onChange={
                                                                            onCommunicationPurposeChange
                                                                        }
                                                                        disabled={
                                                                            !isAccountEnabled
                                                                        }
                                                                    />
                                                                </div>
                                                                {/* <div className="dropdown-width me-3">
                                                        <Dropdown
                                                            id="communicationType"
                                                            defaultValue="Select Option"
                                                            options={[
                                                            { value: 'sms', label: 'SMS' },
                                                            { value: 'email', label: 'Email' },
                                                            ]}
                                                            onChange={onCommunicationTypeChange}
                                                            disabled={!isAccountEnabled}
                                                        />
                                                        </div> */}
                                                                <button
                                                                    onClick={
                                                                        handleSendCommunication
                                                                    }
                                                                    type="button"
                                                                    className="button-base ing-button-primary-left align-middle"
                                                                    disabled={
                                                                        !isAccountEnabled
                                                                    }
                                                                >
                                                                    {getText(
                                                                        'msg_9bc2575c3930',
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="form-row mt-5 account-access-row--password">
                                                        <div className="form-group align-self-baseline">
                                                            <label htmlFor="password">
                                                                {getText(
                                                                    'msg_208bb0d762ab',
                                                                )}{' '}
                                                                <Tooltip
                                                                    html="Setting a new password will immediately update the user's credentials. The user will need to use this new password on their next login."
                                                                    placement="right"
                                                                >
                                                                    <i
                                                                        className="bi bi-info-circle"
                                                                        style={{
                                                                            paddingLeft:
                                                                                '5px',
                                                                            fontSize:
                                                                                '13px',
                                                                            color: 'var(--iNGAGED-Primary-Color)',
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </label>
                                                            <PasswordInput
                                                                id="password1"
                                                                name="password1"
                                                                value={
                                                                    password1
                                                                }
                                                                onChange={
                                                                    handlePasswordChange
                                                                }
                                                                placeholder={getText(
                                                                    'msg_4cf96dca97ad',
                                                                )}
                                                                onFocus={
                                                                    handlePasswordFocus
                                                                }
                                                                onBlur={
                                                                    handlePasswordBlur
                                                                }
                                                                ref={
                                                                    passwordRef
                                                                }
                                                                disabled={
                                                                    !isAccountEnabled
                                                                }
                                                            />
                                                            {showPasswordRequirements && (
                                                                <div className="password-requirements mt-2">
                                                                    {renderPasswordRequirement(
                                                                        'length',
                                                                        `Between ${companyConfig.application?.password_min_length}-${companyConfig.application?.password_max_length} characters`,
                                                                    )}
                                                                    {renderPasswordRequirement(
                                                                        'uppercase',
                                                                        'Contains at least 1 uppercase letter',
                                                                    )}
                                                                    {renderPasswordRequirement(
                                                                        'lowercase',
                                                                        'Contains at least 1 lowercase letter',
                                                                    )}
                                                                    {renderPasswordRequirement(
                                                                        'number',
                                                                        'Contains at least 1 number',
                                                                    )}
                                                                    {renderPasswordRequirement(
                                                                        'special',
                                                                        'Contains at least 1 special character',
                                                                    )}
                                                                    {companyConfig
                                                                        .application
                                                                        ?.password_allow_duplicate_values ||
                                                                        renderPasswordRequirement(
                                                                            'repeats',
                                                                            'No 4 consecutive repeating characters',
                                                                        )}
                                                                    {companyConfig
                                                                        .application
                                                                        ?.password_allow_duplicate_values ||
                                                                        renderPasswordRequirement(
                                                                            'sequential',
                                                                            'No 4 sequential characters',
                                                                        )}
                                                                    {companyConfig
                                                                        .application
                                                                        ?.password_allow_duplicate_values ||
                                                                        renderPasswordRequirement(
                                                                            'patterns',
                                                                            'No repeating patterns',
                                                                        )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="form-group align-self-baseline">
                                                            <label htmlFor="password">
                                                                {getText(
                                                                    'msg_378e8b36fd0b',
                                                                )}
                                                            </label>
                                                            <PasswordInput
                                                                id="password"
                                                                name="password"
                                                                value={password}
                                                                onChange={
                                                                    handleConfirmPasswordChange
                                                                }
                                                                placeholder={getText(
                                                                    'msg_107a96fb1ef1',
                                                                )}
                                                                errorMessage={
                                                                    passwordError
                                                                }
                                                                isConfirmField={
                                                                    true
                                                                }
                                                                originalPassword={
                                                                    password1
                                                                }
                                                                showMatchFeedback={
                                                                    true
                                                                }
                                                                disabled={
                                                                    !isAccountEnabled
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="container user-account-container user-account-access-container">
                                        <div className="account-access">
                                            <div className="access-row row">
                                                <div className="form-group col-6">
                                                    <label htmlFor="accountAccessEndDate">
                                                        {getText(
                                                            'msg_84b147812589',
                                                        )}
                                                        <Tooltip
                                                            html="Select the date when the account access should end."
                                                            placement="right"
                                                        >
                                                            <i
                                                                className="bi bi-info-circle"
                                                                style={{
                                                                    paddingLeft:
                                                                        '5px',
                                                                    fontSize:
                                                                        '13px',
                                                                    color: 'var(--iNGAGED-Primary-Color)',
                                                                    cursor: 'pointer',
                                                                }}
                                                            ></i>
                                                        </Tooltip>
                                                    </label>
                                                    <CustomDatePicker
                                                        selectedDate={
                                                            accountAccessEndDate
                                                        }
                                                        onDateChange={date =>
                                                            setAccountAccessEndDate(
                                                                date,
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="col-6"></div>
                                            </div>
                                            <div className="account-access-create-communication">
                                                <div
                                                    className="checkbox-wrapper"
                                                    style={{
                                                        marginTop:
                                                            '0px !important',
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input custom-checkbox"
                                                        checked={
                                                            sendRegistrationOnCreate
                                                        }
                                                        onChange={event =>
                                                            setSendRegistrationOnCreate(
                                                                event.target
                                                                    .checked,
                                                            )
                                                        }
                                                    />
                                                    <span
                                                        style={{
                                                            color: '#2F2B43',
                                                            fontWeight: '500',
                                                            fontSize: '16px',
                                                        }}
                                                    >
                                                        {getText(
                                                            'msg_f0ce7ab678f1',
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="account-access-create-delivery">
                                                <div className="dropdown-width">
                                                    <Dropdown
                                                        defaultValue={getDeliveryTypeLabel(
                                                            registrationType,
                                                        )}
                                                        options={[
                                                            {
                                                                value: 1,
                                                                label: getText(
                                                                    'msg_9e10d4517d5a',
                                                                ),
                                                            },
                                                            {
                                                                value: 2,
                                                                label: getText(
                                                                    'msg_84add5b29527',
                                                                ),
                                                            },
                                                        ]}
                                                        onChange={
                                                            onRegistrationTypeChange
                                                        }
                                                        disabled={
                                                            !isAccountEnabled ||
                                                            !sendRegistrationOnCreate
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="container user-account-container user-account-footer-container">
                                    <div className="account-access">
                                        <div
                                            className="d-flex justify-content-between align-items-center"
                                            style={{ marginTop: '60px' }}
                                        >
                                            <p>
                                                <span
                                                    className="text-danger"
                                                    style={{
                                                        paddingLeft: '48px',
                                                    }}
                                                >
                                                    *
                                                </span>{' '}
                                                {getText('msg_7c118f042eaa')}
                                            </p>
                                            {canEditForm && (
                                                <button
                                                    type="button"
                                                    className="button-base ing-button-primary-left align-self-center px-5"
                                                    onClick={() => {
                                                        if (
                                                            !isAccountEnabled &&
                                                            !(
                                                                password1 &&
                                                                password1.trim() !==
                                                                    ''
                                                            )
                                                        ) {
                                                            Toast.error(
                                                                getText(
                                                                    'msg_66aa8e9b0915',
                                                                ),
                                                            );
                                                            return;
                                                        }
                                                        handleSave();
                                                    }}
                                                >
                                                    {getText(
                                                        'msg_efc007a393f6',
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAccountView;
